import React, { FC, useMemo } from 'react';
import styles from './index.module.scss';
import { BidsDashboardCardMode, IBids } from 'src/models/bids/Bids/types';
import { BidCategoryBadge } from 'src/common/BidCategoryBadge';
import { useBidWorkspace } from 'src/models/bids/Bids/hooks';
import { Tooltip, useLanguage, WorkspaceBadge } from 'src/common';
import { useOpenBidPreview } from 'src/pages/BidsPage/BidsBoard/hooks';
import { useCurrentSidebar } from 'src/shared/InfoSidebar/hooks';
import { SidebarMode } from 'src/shared/InfoSidebar/types';
import classNames from 'classnames';
import Deadline from 'src/common/Deadline';
import Assignee from 'src/shared/Assignee';
import { useTranslation } from 'react-i18next';
import { formatCurrencyValue } from 'src/models/procurements/Tender/helpers';
import { formatDate } from 'src/helpers/dates';

interface Props {
  bid: IBids;
  mode: BidsDashboardCardMode;
}

export const BidListItem: FC<Props> = props => {
  const { bid, mode } = props;
  const { assignedTo, id, origin, status } = bid;
  const { name, deadline } = origin;

  const { t } = useTranslation();
  const { language } = useLanguage();
  const { workspace } = useBidWorkspace(id);
  const openPreview = useOpenBidPreview(bid);
  const currentSidebar = useCurrentSidebar();

  const isActive = useMemo(
    () => currentSidebar?.mode === SidebarMode.BID_INFO && currentSidebar.id === id,
    [currentSidebar, id]
  );

  return (
    <div className={classNames(styles.item, { [styles.isActive]: isActive })} key={id} onClick={e => openPreview(e)}>
      <div className={styles.titleContainer}>
        <div className={styles.wsBadge}>
          <WorkspaceBadge title={workspace?.name ?? ''} color={workspace?.color ?? ''} mode={'short'} />
        </div>
        <Tooltip title={name}>
          <div className={styles.title}>{name}</div>
        </Tooltip>
      </div>
      <div className={styles.infoContainer}>
        {mode === BidsDashboardCardMode.forecastByContractStart && (
          <div className={styles.itemInfo}>
            <Assignee assignedToId={assignedTo} size={'s'} mode="onlyAvatar" />

            <div className={styles.container}>
              <span className={styles.greyMiColor}>{t('Tenders.contractStart')}</span>
              <span>
                {origin.contractStart && formatDate(origin.contractStart, 'year', { showRangeDateFull: true })}
              </span>
            </div>

            <div className={styles.container}>
              <span className={styles.greyMiColor}>{t('Tenders.estValue')}</span>
              <span>
                {formatCurrencyValue(
                  origin.estimatedValue?.amount ?? 0,
                  language,
                  origin.estimatedValue?.currency ?? ''
                )}
              </span>
            </div>
          </div>
        )}
        {status && <BidCategoryBadge status={status} />}
        {deadline && mode !== BidsDashboardCardMode.forecastByContractStart && (
          <div className={styles.date}>
            <div className={classNames(styles.value)}>
              <Deadline deadline={deadline} showIcon />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BidListItem;
